import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn.js'

/**
 * The type scale, as components rather than as remembered class strings.
 *
 * The archive's whole reason for existing is that the same card was retyped forty
 * times and diverged on each pass. Type is where that starts: eleven roles, each
 * with a size, a weight and an ink, and no screen chooses them itself.
 */
const text = cva('', {
  variants: {
    /**
     * Named `variant`, not `role`.
     *
     * `role` is an HTML attribute with a meaning of its own, and a component that
     * takes both cannot be typed — the compiler said so. It is also the confusion
     * that produced an error message with two `role` props on it half an hour
     * earlier. One name, one meaning.
     */
    variant: {
      display: 'text-display font-semibold text-ink leading-tight',
      title: 'text-title font-semibold text-ink leading-snug',
      'section-title': 'text-body font-semibold text-ink',
      'card-title': 'text-body font-semibold text-ink',
      body: 'text-body text-ink',
      'body-strong': 'text-body font-medium text-ink',
      secondary: 'text-second text-ink-2',
      caption: 'text-caption text-ink-muted',
      micro: 'text-micro text-ink-muted',
      /** Small, spaced, above a heading. Names the section rather than titling it. */
      eyebrow: 'text-micro font-medium uppercase tracking-wide text-ink-muted',
      /** Under a field, saying what is expected. Never used for an error. */
      hint: 'text-second text-ink-muted',
    },
  },
  defaultVariants: { variant: 'body' },
})

type Element = 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'label'

export interface TextProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof text> {
  /**
   * The tag to render. Kept separate from `role` on purpose: how big a thing looks
   * and what it *is* to a screen reader are two questions, and tying them together
   * is how a page ends up with six `h1`s because six things were the same size.
   */
  as?: Element
}

export function Text({ as: Tag = 'p', variant, className, ...props }: TextProps) {
  return <Tag className={cn(text({ variant }), className)} {...props} />
}

/** The common roles, so a screen reads as prose rather than as configuration. */
export const Display = (p: Omit<TextProps, 'variant'>) => <Text as="h1" variant="display" {...p} />
export const Title = (p: Omit<TextProps, 'variant'>) => <Text as="h2" variant="title" {...p} />
export const SectionTitle = (p: Omit<TextProps, 'variant'>) => (
  <Text as="h3" variant="section-title" {...p} />
)
export const Body = (p: Omit<TextProps, 'variant'>) => <Text variant="body" {...p} />
export const BodyStrong = (p: Omit<TextProps, 'variant'>) => <Text variant="body-strong" {...p} />
export const Secondary = (p: Omit<TextProps, 'variant'>) => <Text variant="secondary" {...p} />
export const Caption = (p: Omit<TextProps, 'variant'>) => <Text variant="caption" {...p} />
export const Micro = (p: Omit<TextProps, 'variant'>) => <Text variant="micro" {...p} />
export const Eyebrow = (p: Omit<TextProps, 'variant'>) => <Text variant="eyebrow" {...p} />
export const Hint = (p: Omit<TextProps, 'variant'>) => <Text variant="hint" {...p} />
